import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { MessageService } from 'primeng/api';
import { Observable, lastValueFrom, map, catchError } from 'rxjs';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { getReadableFileSize } from '../../../../Core/utils';
import { allowedFileTypes, FileUploadModel } from '../../models/file-upload.model';
import { download } from '../../../../Common/Misc/helpers/download/download.helper';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaServiceErrorCodesEnum } from '../../../../api/mediaService/mediaResults.api.model';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { resolveFileIcon } from '../../../Misc/helpers/file/fileIcon.helper';
import { UploadOption } from '../../models/upload-option.model';

@Component({
    selector: 'agpr-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        {
            provide: MediaServiceApi,
            useClass: MediaServiceApi
        },
        getProvider(FileUploadComponent)
    ],
    standalone: false
})
export class FileUploadComponent extends ControlValueAccessorModel<FileUploadModel[]> {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() public id: string;
    @Input() public maxFiles: number = 5;
    @Input() public uploadButtonIcon: string;
    @Input() public maxFileSize: number = (1024 * 1024 * 2); // 2MB
    @Input() public maxFileSizeCombined: number = (1024 * 1024 * 5); // 5MB
    @Input() public header: string = 'File Upload';
    @Input() public headerTooltip: string = null;
    @Input() public uploadButtonLabel: string = 'Upload File';
    @Input() public placeholder: string = 'Discover.fileUpload.noFilePlaceholder';
    @Input() public allowDuplicates: boolean = false;
    @Input() public allowMultiple: boolean = true;
    @Input() useContentLibraryUpload: boolean = false;
    @Input() public holdFileData: boolean = false;
    @Input() public panelToggleable: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public uppercaseHeader: boolean = true;
    @Input() public validationFunction: (file: File) => Observable<boolean>;
    @Input() public shownoContentIcon: boolean = true;
    @Input() public showHeaderButton: boolean = true;
    @Input() public showSubHeader: boolean = false;
    @Input() public showThumbnail: boolean = false;
    @Input() showPanelSubHeader: boolean = false;
    @Input() showPanelCustomHeader: boolean = true;

    /**
     * Regex string for validating uploaded file types
     */
    @Input() public supportedFileTypesRegex: string = allowedFileTypes.allMIMETypesRegex;
    @Input() public supportedFileTypes: string;
    @Input() public uploadOptions: UploadOption[];

    /**
     * Allows downloading the file to your computer when the file chip is clicked.
     * If set to true, automatically also sets holdFileData to true.
     * Files must be uploaded by the parent component by calling uploadFiles() before they can be downloaded,
     * as there must be a download url supplied by backend.
     */
    @Input() public set allowFileDownload(allowFileDownload: boolean) {
        this._allowFileDownload = allowFileDownload;
        if (this._allowFileDownload) {
            this.holdFileData = true;
        }
    }

    /* eslint-disable @angular-eslint/no-output-on-prefix */
    @Output() onRemove: EventEmitter<any> = new EventEmitter();
    @Output() onAdd: EventEmitter<any> = new EventEmitter();
    @Output() onValidationError: EventEmitter<any> = new EventEmitter();
    @Output() uploadButtonClick = new EventEmitter<void>();

    value: FileUploadModel[] = [];
    _allowFileDownload: boolean = false;

    private combinedFileSize: number = 0;

    constructor(
        private mediaServiceApi: MediaServiceApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private httpClient: HttpClient
    ) {
        super();
    }

    async addNewFile(event): Promise<void> {
        const fileList: FileList = event.target.files;
        const successfulFiles: FileUploadModel[] = [];
        const validationErrors: FileUploadModel[] = [];

        for (let i = 0; i < fileList.length; i++) {
            const file: File = fileList[i];
            if (this.value.length >= this.maxFiles) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.tooManyFiles.summary'),
                    detail: this.localizationService.transform(
                        'Common.forms.fileUpload.error.tooManyFiles.detail',
                        { files: this.maxFiles }
                    )
                });
                break;
            } else if ((file.size + this.combinedFileSize) >= this.maxFileSizeCombined) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.combinedFileSize.summary'),
                    detail: this.localizationService.transform(
                        'Common.forms.fileUpload.error.combinedFileSize.detail',
                        { 'size': getReadableFileSize(this.maxFileSizeCombined) }
                    )
                });
                break;
            } else if (!file.type.match(this.supportedFileTypesRegex)) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.mimeType.summary'),
                    detail: this.localizationService.transform('Common.forms.fileUpload.error.mimeType.detail')
                });
            } else if (!this.allowDuplicates && this.isDuplicate(file)) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.duplicate.summary'),
                    detail: this.localizationService.transform('Common.forms.fileUpload.error.duplicate.detail')
                });
            } else if (file.size > this.maxFileSize) {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.forms.fileUpload.error.fileSize.summary'),
                    detail: this.localizationService.transform(
                        'Common.forms.fileUpload.error.fileSize.detail',
                        { 'size': getReadableFileSize(this.maxFileSize) }
                    )
                });
            } else {
                if (!this.validationFunction) {
                    successfulFiles.push(this.pushFile(file));
                } else {
                    const isValid = await lastValueFrom(this.validationFunction(file));
                    if (isValid) {
                        successfulFiles.push(this.pushFile(file));
                    } else {
                        validationErrors.push(file);
                    }
                }
            }
        }

        if (successfulFiles.length > 0) {
            this.onAdd.emit(successfulFiles);
            this.onModelChange(this.value);
        }

        if (validationErrors.length > 0) {
            this.onValidationError.emit(validationErrors);
        }

        event.target.value = null;
    }

    private pushFile(file: File): FileUploadModel {
        const newFile = {
            name: file.name,
            type: file.type,
            size: file.size,
            file: this.holdFileData ? file : null
        };
        this.value.push(newFile);
        this.combinedFileSize += newFile.size;

        return newFile;
    }

    async removeFile(index: number) {
        const [removedFile] = this.value.splice(index, 1);
        this.combinedFileSize -= removedFile.size;
        /*
        Commenting out the delete for now, it's causing issues when other resources are pointing to a now deleted file, we should investigate re-enabling it
        as part of the work to create a 'file library' in NEP-7834
        if (removedFile.id) {
            await this.removeFileFromMediaService(removedFile.id);
        }
        */
        this.onRemove.emit(removedFile);
        this.onModelChange(this.value);
    }

    private removeFileFromMediaService(id: string) {
        return new Promise<void | string>(
            (resolve, reject) => {
                this.mediaServiceApi.delete(id).subscribe(response => {
                    if (response === null) {
                        resolve();
                    } else {
                        reject('Failed to remove file from media service');
                    }
                });
            }
        );
    }

    downloadFile(file: FileUploadModel): void {
        if (!file.url) {
            this.messageService.add({
                severity : messageSeverityEnum.warning,
                summary: this.localizationService.transform('Common.forms.fileUpload.warning.fileNotUploaded.summary'),
                detail: this.localizationService.transform('Common.forms.fileUpload.warning.fileNotUploaded.detail')
            });
            return;
        }

        let headers = new HttpHeaders;
        headers = headers.set('content-type', file.type);
        this.httpClient.get(file.url, {'headers': headers, responseType: 'blob'}).subscribe(
            result => {
                download.blob(result, file.name);
            }
        );
    }

    uploadFiles(accessType: MediaServiceAccessTypeEnum = MediaServiceAccessTypeEnum.PRIVATE): Promise<FileUploadModel[]> {
        if (!this.holdFileData) {
            return Promise.reject('No file data to upload');
        }

        const filesAlreadyUploaded: FileUploadModel[] = this.value.filter(file => !!file.id);
        // All files without an id have yet to be uploaded
        const filesToUpload: File[] = this.value.filter(file => !file.id).map(file => file.file);

        if (filesToUpload.length === 0) {
            return Promise.resolve(this.value);
        }

        return lastValueFrom(
            this.mediaServiceApi.upload(filesToUpload, accessType).pipe(
                catchError(error => {
                    if (error.status === 429) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                            detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: filesToUpload[0]?.name })
                        });
                    }
                    throw error;
                }),
                map(mediaResults => {
                    this.value = filesAlreadyUploaded;
                    mediaResults.mediaResults.forEach(result => {
                        if (!result.codedError) {
                            this.value.push({
                                id: result.media.id,
                                name: result.media.name,
                                size: result.media.size,
                                type: result.media.mimeType,
                                url: result.media.url
                            });
                        } else {
                            this.addUploadErrorMessage(result.media.name, result.codedError);
                        }
                    });
                    return this.value;
                })
            )
        );
    }

    addUploadErrorMessage(resultName, error) {
        let summaryMessage;
        let detailMessage;
        switch (error.code) {
            case MediaServiceErrorCodesEnum.invalidType:
                summaryMessage = 'Common.mediaService.fileUpload.typeError.heading';
                detailMessage = 'Common.mediaService.fileUpload.typeError.message';
                break;
            case MediaServiceErrorCodesEnum.fileMaxSize:
                summaryMessage = 'Common.mediaService.fileUpload.maxSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.maxSize.message';
                break;
            case MediaServiceErrorCodesEnum.insufficientSpace:
                summaryMessage = 'Common.mediaService.fileUpload.noSpace.heading';
                detailMessage = 'Common.mediaService.fileUpload.noSpace.message';
                break;
            case MediaServiceErrorCodesEnum.fileMinSize:
                summaryMessage = 'Common.mediaService.fileUpload.minSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.minSize.message';
                break;
            case MediaServiceErrorCodesEnum.internalError:
            default:
                if (error.status === 429) {
                    summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                    detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                } else {
                    summaryMessage = 'Common.mediaService.fileUpload.internalError.heading';
                    detailMessage = 'Common.mediaService.fileUpload.internalError.message';
                }
                break;
        }

        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary: this.localizationService.transform(summaryMessage),
            detail: this.localizationService.transform(detailMessage, { name: resultName })
        });
    }

    private isDuplicate(possibleDuplicate: FileUploadModel): boolean {
        return this.value.some(file => file.name === possibleDuplicate.name);
    }

    writeValue(value: FileUploadModel[] = []): void {
        this.value = value;
    }
    getFileIcon(mimeType: string): string {
        return resolveFileIcon(mimeType);
    }
}
